import { useIsFirstRender } from '@mantine/hooks';
import classNames from 'classnames/bind';
import { Children, type PropsWithChildren, useEffect, useState } from 'react';
import { useIsMobile } from 'shared/hooks/utils/use-is-mobile';

import { Icon, IconName } from '../Icon';

import styleIdentifiers from './Tabs.module.scss';

const styles = classNames.bind(styleIdentifiers);

type Props = {
  activeClassName?: string;
  className?: string;
  tabs: any[];
  noBody?: boolean;
  noRounded?: boolean;
  onChange?: (newTabIndex: number, oldTabIndex: number) => void;
  tabIndex: number;
};

export default function Tabs({
  activeClassName,
  children,
  className,
  tabs,
  noBody = false,
  noRounded,
  onChange,
  tabIndex,
}: PropsWithChildren<Props>) {
  const isMobile = useIsMobile();
  const [currentIndex, setCurrentIndex] = useState(tabIndex || 0);
  const isFirstRender = useIsFirstRender();

  useEffect(() => {
    if (tabIndex && !isFirstRender) setActive(tabIndex)();
  }, [tabIndex]);

  const setActive = (index: number) => () => {
    onChange?.(index, currentIndex);
    setCurrentIndex(index);
  };

  const nextSlide = (e: any) => {
    e.stopPropagation();
    onChange?.(currentIndex + 1, currentIndex);
    setCurrentIndex(currentIndex + 1);
  };

  const previousSlide = (e: any) => {
    e.stopPropagation();
    onChange?.(currentIndex - 1, currentIndex);
    setCurrentIndex(currentIndex - 1);
  };

  return (
    <div className={styles('tabs', noRounded && 'no-rounded', noBody && 'noBody', className)}>
      <div
        className={styles('header')}
        style={{
          transform: isMobile ? `translate( -${currentIndex}00%)` : '',
        }}
      >
        {tabs.map((item, index) => (
          <div
            className={styles(
              'header-item',
              currentIndex === index && 'active',
              currentIndex === index && activeClassName,
            )}
            key={index}
            onClick={setActive(index)}
          >
            {index !== 0 && (
              <Icon
                name={IconName.MINIMAL_LEFT}
                onClick={previousSlide}
                className={styles('prev')}
              />
            )}
            <div className={styles('children')}>{item}</div>

            {tabs.length - 1 !== currentIndex && (
              <Icon name={IconName.MINIMAL_RIGHT} onClick={nextSlide} className={styles('next')} />
            )}

            <div className={styles('line')} />
          </div>
        ))}
      </div>
      {!noBody && <div className={styles('body')}>{Children.toArray(children)[tabIndex]}</div>}
    </div>
  );
}
